"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { SkeletonV2 } from "./SkeletonV2";

export interface TableColumn<T> {
  key: string;
  header?: React.ReactNode;
  title?: string;
  render?: (_row: T, _index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  width?: string;
}

export interface TableV2Props<T> {
  columns: TableColumn<T>[];
  rows?: T[];
  data?: T[];
  rowKey?: (_row: T, _index: number) => string;
  onRowClick?: (_row: T, _index: number) => void;
  emptyText?: string;
  loading?: boolean;
  caption?: string;
  dense?: boolean;
  striped?: boolean;
  className?: string;
}

function TableV2Inner<T>(
  {
    columns = [],
    rows,
    data,
    rowKey,
    onRowClick,
    emptyText = "Kayıt bulunamadı",
    loading = false,
    caption,
    dense = false,
    striped = false,
    className,
  }: TableV2Props<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const cellPad = dense ? "px-3 py-1.5" : "px-4 py-3";
  const skeletonRows = dense ? 8 : 5;
  const resolvedRows = React.useMemo(() => rows ?? data ?? [], [data, rows]);
  const resolvedColumns = React.useMemo(
    () => (Array.isArray(columns) ? columns : []),
    [columns]
  );
  const resolvedRowKey = React.useCallback(
    (row: T, index: number) => {
      if (rowKey) return rowKey(row, index);
      const candidate =
        typeof row === "object" && row !== null && "id" in (row as object)
          ? (row as { id?: unknown }).id
          : undefined;
      return String(candidate ?? index);
    },
    [rowKey]
  );

  return (
    <div
      ref={ref}
      className={cn("v2-panel overflow-x-auto", className)}
      role="region"
      aria-label={caption || "Veri tablosu"}
      tabIndex={0}
    >
      <table className="w-full min-w-[480px] border-collapse text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-[var(--v2-border)]">
            {resolvedColumns.map((col) => (
              <th
                key={col.key}
                scope="col"
                title={col.title}
                className={cn(
                  cellPad,
                  "text-left text-xs font-semibold uppercase tracking-wide text-[var(--v2-text-muted)]",
                  col.headerClassName
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header || col.title || col.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <tr
                key={`skel-${String(i)}`}
                className="border-b border-[var(--v2-border)] last:border-b-0"
              >
                {resolvedColumns.map((col) => (
                  <td key={col.key} className={cellPad}>
                    <SkeletonV2 className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : resolvedRows.length === 0 ? (
            <tr>
              <td
                colSpan={Math.max(1, resolvedColumns.length)}
                className="px-4 py-10 text-center text-sm text-[var(--v2-text-muted)]"
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            resolvedRows.map((row, rowIdx) => (
              <tr
                key={resolvedRowKey(row, rowIdx)}
                className={cn(
                  "border-b border-[var(--v2-border)] last:border-b-0 transition-colors hover:bg-slate-50/60",
                  onRowClick && "cursor-pointer",
                  striped && rowIdx % 2 === 1 && "bg-slate-50/40"
                )}
                onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
              >
                {resolvedColumns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      cellPad,
                      "text-[var(--v2-text-strong)]",
                      col.className
                    )}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.render
                      ? col.render(row, rowIdx)
                      : String((row as Record<string, unknown>)[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export const TableV2 = React.forwardRef(TableV2Inner) as <T>(
  _props: TableV2Props<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;
